import { apiService } from './api-service';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: {
    id: string;
    username: string;
    email: string;
    role: number;
    role_name: string;
  };
}

export interface User {
  id: string;
  username: string;
  email: string;
  full_name?: string;
  role: number;
  role_name: string;
  permissions?: Permission[];
  created_at: number;
}

export interface Permission {
  id: string;
  name: string;
  description: string;
  category: string;
  isGranted: boolean;
}

export interface RefreshTokenResponse {
  token: string;
}

export class AuthService {
  private static readonly TOKEN_KEY = 'auth_token';
  private static readonly USER_KEY = 'auth_user';

  /**
   * Login with username and password
   */
  static async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://obta-api.trade.workers.dev'}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Invalid username or password');
        }
        throw new Error(`Login failed: ${response.status} ${response.statusText}`);
      }

      const data: LoginResponse = await response.json();
      
      // Store token and user info securely
      this.setToken(data.token);
      this.setUser(data.user);
      
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  /**
   * Logout the current user
   */
  static async logout(): Promise<void> {
    try {
      const token = this.getToken();
      if (token) {
        // Call API logout endpoint to invalidate token server-side
        await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://obta-api.trade.workers.dev'}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with local cleanup even if API call fails
    } finally {
      // Always clear local storage
      this.clearAuth();
    }
  }

  /**
   * Refresh the current JWT token
   */
  static async refreshToken(): Promise<string> {
    try {
      const currentToken = this.getToken();
      if (!currentToken) {
        throw new Error('No token to refresh');
      }

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://obta-api.trade.workers.dev'}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.status} ${response.statusText}`);
      }

      const data: RefreshTokenResponse = await response.json();
      this.setToken(data.token);
      
      return data.token;
    } catch (error) {
      console.error('Token refresh error:', error);
      // Clear auth if refresh fails
      this.clearAuth();
      throw error;
    }
  }

  /**
   * Get current user information
   */
  static async getCurrentUser(): Promise<User> {
    try {
      const response = await apiService.get('/users/me');
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication required');
        }
        throw new Error(`Failed to get user info: ${response.status} ${response.statusText}`);
      }

      const user: User = await response.json();
      this.setUser(user);
      
      return user;
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  }

  /**
   * Validate the current token
   */
  static async validateToken(): Promise<boolean> {
    try {
      const token = this.getToken();
      if (!token) {
        return false;
      }

      // Try to get current user to validate token
      await this.getCurrentUser();
      return true;
    } catch (error) {
      console.error('Token validation failed:', error);
      this.clearAuth();
      return false;
    }
  }

  /**
   * Check if user is currently authenticated
   */
  static isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getStoredUser();
    return !!(token && user);
  }

  /**
   * Get stored JWT token
   */
  static getToken(): string | null {
    try {
      return localStorage.getItem(this.TOKEN_KEY);
    } catch (error) {
      console.error('Failed to get token from localStorage:', error);
      return null;
    }
  }

  /**
   * Get stored user information
   */
  static getStoredUser(): User | null {
    try {
      const userStr = localStorage.getItem(this.USER_KEY);
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Failed to get user from localStorage:', error);
      return null;
    }
  }

  /**
   * Store JWT token securely
   */
  private static setToken(token: string): void {
    try {
      localStorage.setItem(this.TOKEN_KEY, token);
    } catch (error) {
      console.error('Failed to store token:', error);
      throw new Error('Failed to store authentication token');
    }
  }

  /**
   * Store user information
   */
  private static setUser(user: User | LoginResponse['user']): void {
    try {
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    } catch (error) {
      console.error('Failed to store user info:', error);
      throw new Error('Failed to store user information');
    }
  }

  /**
   * Clear all authentication data
   */
  private static clearAuth(): void {
    try {
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.USER_KEY);
    } catch (error) {
      console.error('Failed to clear auth data:', error);
    }
  }

  /**
   * Check if user has a specific permission
   */
  static hasPermission(permissionId: string): boolean {
    const user = this.getStoredUser();
    if (!user || !user.permissions) {
      return false;
    }
    
    const permission = user.permissions.find(p => p.id === permissionId);
    return permission ? permission.isGranted : false;
  }

  /**
   * Check if user has a specific role
   */
  static hasRole(roleId: number): boolean {
    const user = this.getStoredUser();
    return user ? user.role === roleId : false;
  }
}

export default AuthService;
